/**
 * Single-flight loader for the Google Maps JavaScript API.
 * Returns a promise that resolves to the global `google` namespace once the
 * script is ready. Subsequent calls reuse the same promise, so calling this
 * from multiple components won't inject duplicate `<script>` tags.
 *
 * Requires REACT_APP_GOOGLE_MAPS_KEY + Maps JavaScript API enabled on the
 * Cloud project.
 */
let loadPromise = null;

export const loadGoogleMaps = (libraries = []) => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("loadGoogleMaps called outside a browser"));
  }
  if (window.google?.maps) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!apiKey) {
    return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_KEY"));
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    const libs = libraries.length
      ? `&libraries=${libraries.join(",")}`
      : "";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}${libs}&loading=async&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => {
      loadPromise = null;
      reject(new Error("Failed to load Google Maps JS API"));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};
